import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Edit,
  FolderPlus,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { mapSaveAccountResult, parseApiList } from '../../utils/accountingHelpers';

const panelClass = 'rounded-xl border border-gray-200 bg-white shadow-sm';
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function AccountTreeNode({ node, expandedIds, onToggle, onAddGroup, onAddAccount, onEditGroup, onEditAccount, onDelete }) {
  const isGroup = node.IsGroup !== false;
  const isExpanded = expandedIds.has(node.Id);

  return (
    <li className="ml-3 border-l border-gray-200 pl-3">
      <div className="flex flex-wrap items-center gap-2 py-1.5">
        {isGroup ? (
          <button type="button" className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100" onClick={() => onToggle(node)}>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="inline-flex h-5 w-5 items-center justify-center text-emerald-600">●</span>
        )}
        <span className={`flex-1 text-sm ${isGroup ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
          {node.Name}
          {node.AccountCode ? <span className="ml-2 text-xs text-gray-500">[{node.AccountCode}]</span> : null}
        </span>
        {isGroup ? (
          <>
            <button type="button" title="Add sub-group" className="rounded p-1 text-primary hover:bg-primary/10" onClick={() => onAddGroup(node)}>
              <FolderPlus className="h-4 w-4" />
            </button>
            <button type="button" title="Add ledger account" className="rounded p-1 text-emerald-600 hover:bg-emerald-50" onClick={() => onAddAccount(node)}>
              <Plus className="h-4 w-4" />
            </button>
            <button type="button" title="Edit group" className="rounded p-1 text-gray-600 hover:bg-gray-100" onClick={() => onEditGroup(node)}>
              <Edit className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <button type="button" title="Edit account" className="rounded p-1 text-gray-600 hover:bg-gray-100" onClick={() => onEditAccount(node)}>
              <Edit className="h-4 w-4" />
            </button>
            <button type="button" title="Delete account" className="rounded p-1 text-red-600 hover:bg-red-50" onClick={() => onDelete(node)}>
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
      {isGroup && isExpanded && node.Children?.length ? (
        <ul className="space-y-1">
          {node.Children.map((child) => (
            <AccountTreeNode
              key={child.Id}
              node={child}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onAddGroup={onAddGroup}
              onAddAccount={onAddAccount}
              onEditGroup={onEditGroup}
              onEditAccount={onEditAccount}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

const emptyGroupForm = (overrides = {}) => ({
  Id: 0,
  Name: '',
  NameAr: '',
  ParentId: '',
  IsActive: true,
  IsGroup: true,
  Level: 1,
  AccountTypeId: '',
  ...overrides,
});

const emptyAccountForm = (overrides = {}) => ({
  Id: 0,
  Name: '',
  NameAr: '',
  ParentId: '',
  IsActive: true,
  IsGroup: false,
  Level: 2,
  AccountTypeId: '',
  Description: '',
  VATType: '',
  VATId: '',
  ...overrides,
});

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [accTypes, setAccTypes] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyGroupForm());
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadTypes = useCallback(async () => {
    try {
      setAccTypes(parseApiList(await MainService.AccountTypeAllChilds()));
    } catch {
      setAccTypes([]);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = isSearch
        ? await MainService.AccountListByKeyword({ Keyword: keyword })
        : await MainService.AccountList();
      setAccounts(parseApiList(response));
    } catch {
      setError('Failed to load chart of accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [isSearch, keyword]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const attachChildren = (list, parentId, children) =>
    list.map((item) => {
      if (item.Id === parentId) return { ...item, Children: children };
      if (item.Children) return { ...item, Children: attachChildren(item.Children, parentId, children) };
      return item;
    });

  const toggleExpand = async (node) => {
    const next = new Set(expandedIds);
    if (next.has(node.Id)) {
      next.delete(node.Id);
      setExpandedIds(next);
      return;
    }
    next.add(node.Id);
    setExpandedIds(next);
    if (node.Children) return;
    try {
      const children = parseApiList(await MainService.AccountSubList(node.Id));
      setAccounts((prev) => attachChildren(prev, node.Id, children));
    } catch {
      setError('Failed to load sub-accounts');
    }
  };

  const loadSimilarTypes = async (accountTypeId) => {
    if (!accountTypeId) return setAccTypes(parseApiList(await MainService.AccountTypeAllChilds()));
    try {
      setAccTypes(parseApiList(await MainService.SimilarAccountTypes(accountTypeId)));
    } catch {
      setAccTypes([]);
    }
  };

  const openMainGroup = () => {
    setForm(emptyGroupForm());
    setModal('main-group');
  };

  const openSubGroup = (parent) => {
    setForm(
      emptyGroupForm({
        ParentId: parent.Id,
        Level: (parent.Level || 1) + 1,
        AccountTypeId: parent.AccountTypeId || '',
      })
    );
    loadSimilarTypes(parent.AccountTypeId);
    setModal('sub-group');
  };

  const openLedger = (parent) => {
    setForm(
      emptyAccountForm({
        ParentId: parent.Id,
        Level: (parent.Level || 1) + 1,
        AccountTypeId: parent.AccountTypeId || '',
      })
    );
    loadSimilarTypes(parent.AccountTypeId);
    setModal('ledger');
  };

  const openEditGroup = (node) => {
    setForm({
      Id: node.Id,
      Name: node.Name || '',
      NameAr: node.NameAr || '',
      ParentId: node.ParentId || '',
      IsActive: node.IsActive !== false,
      IsGroup: true,
      Level: node.Level || 1,
      AccountTypeId: node.AccountTypeId || '',
    });
    loadSimilarTypes(node.AccountTypeId);
    setModal('edit-group');
  };

  const openEditAccount = (node) => {
    setForm({
      Id: node.Id,
      Name: node.Name || '',
      NameAr: node.NameAr || '',
      ParentId: node.ParentId || '',
      IsActive: node.IsActive !== false,
      IsGroup: false,
      Level: node.Level || 2,
      AccountTypeId: node.AccountTypeId || '',
      Description: node.Description || '',
      VATType: node.VATType || '',
      VATId: node.VATId || '',
    });
    loadSimilarTypes(node.AccountTypeId);
    setModal('edit-ledger');
  };

  const closeModal = () => {
    setModal(null);
    setForm(emptyGroupForm());
  };

  const saveGroup = async () => {
    if (!form.Name.trim()) {
      setError('Account name is required');
      return;
    }
    if (!form.AccountTypeId) {
      setError('Select account type');
      return;
    }
    try {
      setLoading(true);
      const api = form.Id > 0 ? MainService.UpdateAccount : MainService.SaveAccounts;
      const result = await api(form);
      const { ok, message } = mapSaveAccountResult(result);
      if (ok) {
        setSuccess(message);
        closeModal();
        setExpandedIds(new Set());
        setIsSearch(false);
        await loadAccounts();
      } else {
        setError(message);
      }
    } catch {
      setError('Failed to save account group');
    } finally {
      setLoading(false);
    }
  };

  const saveLedger = async () => {
    if (!form.Name.trim()) {
      setError('Account name is required');
      return;
    }
    if (!form.AccountTypeId) {
      setError('Select account type');
      return;
    }
    try {
      setLoading(true);
      const payload = { ...form, IsGroup: false };
      const api = form.Id > 0 ? MainService.UpdateAccount : MainService.SaveAccounts;
      const result = await api(payload);
      const { ok, message } = mapSaveAccountResult(result);
      if (ok) {
        setSuccess(message);
        closeModal();
        setExpandedIds(new Set());
        setIsSearch(false);
        await loadAccounts();
      } else {
        setError(message);
      }
    } catch {
      setError('Failed to save ledger account');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      await MainService.DeleteAccountMaster(deleteTarget.Id);
      setSuccess('Account deleted');
      setDeleteTarget(null);
      setExpandedIds(new Set());
      await loadAccounts();
    } catch {
      setError('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const runSearch = () => {
    setIsSearch(Boolean(keyword.trim()));
  };

  const resetSearch = () => {
    setKeyword('');
    setIsSearch(false);
  };

  const modalTitle =
    modal === 'main-group'
      ? 'Add Main Account Group'
      : modal === 'sub-group'
        ? 'Add Sub Group'
        : modal === 'ledger'
          ? 'Add Ledger Account'
          : modal === 'edit-group'
            ? 'Edit Account Group'
            : modal === 'edit-ledger'
              ? 'Edit Ledger Account'
              : '';

  const isLedgerModal = modal === 'ledger' || modal === 'edit-ledger';
  const isEdit = modal === 'edit-group' || modal === 'edit-ledger';

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
          <p className="text-sm text-gray-500">Manage account groups and ledger accounts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={openMainGroup} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
            <FolderPlus className="h-4 w-4" />
            Main Group
          </button>
          <button type="button" onClick={loadAccounts} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className={`${panelClass} flex flex-wrap items-center gap-2 p-4`}>
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by name or code"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          />
        </div>
        <button type="button" onClick={runSearch} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
          Search
        </button>
        {isSearch ? (
          <button type="button" onClick={resetSearch} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Clear
          </button>
        ) : null}
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      ) : null}

      <div className={`${panelClass} p-4`}>
        {loading && !accounts.length ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading accounts…</p>
        ) : accounts.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No accounts found.</p>
        ) : (
          <ul className="space-y-2">
            {accounts.map((node) => (
              <AccountTreeNode
                key={node.Id}
                node={node}
                expandedIds={expandedIds}
                onToggle={toggleExpand}
                onAddGroup={openSubGroup}
                onAddAccount={openLedger}
                onEditGroup={openEditGroup}
                onEditAccount={openEditAccount}
                onDelete={setDeleteTarget}
              />
            ))}
          </ul>
        )}
      </div>

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{modalTitle}</h2>
              <button type="button" onClick={closeModal} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <input className={inputClass} value={form.Name} onChange={(e) => setForm((p) => ({ ...p, Name: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Arabic Name</label>
                <input className={inputClass} value={form.NameAr} onChange={(e) => setForm((p) => ({ ...p, NameAr: e.target.value }))} />
              </div>
              {!isEdit || modal === 'main-group' || modal === 'edit-group' ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Account Type</label>
                  <select
                    className={inputClass}
                    value={form.AccountTypeId || ''}
                    onChange={(e) => setForm((p) => ({ ...p, AccountTypeId: e.target.value }))}
                    disabled={modal === 'sub-group' || modal === 'ledger'}
                  >
                    <option value="">Select type</option>
                    {accTypes.map((t) => (
                      <option key={t.Id} value={t.Id}>
                        {t.Name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {isLedgerModal ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={form.Description}
                    onChange={(e) => setForm((p) => ({ ...p, Description: e.target.value }))}
                  />
                </div>
              ) : null}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.IsActive !== false} onChange={(e) => setForm((p) => ({ ...p, IsActive: e.target.checked }))} />
                Active
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={isLedgerModal ? saveLedger : saveGroup}
                  disabled={loading}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete account?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Delete <strong>{deleteTarget.Name}</strong>? This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={confirmDelete} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ChartOfAccounts;
